import type { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import type { Game, GameTrackingInfo } from '@/types/games';
import type { GameViewModel } from '@/types/games/game-view-model';
import type { RotatingTitles, Zone } from '@/types/zones/zone';
import type { AdaptGameOptions } from '@/logic/game-adapter/adapt-game';
import { getZoneGames } from './zone-games';
import type { ClientGameContext } from './client-game-context';

export type ParsedGameZoneMeta = {
    title?: string;
    categoryUid?: string;
    zoneUid?: string;
    rotatingTitles?: RotatingTitles;
    isDynamicOrdering: boolean;
};

export type ParsedGameZone = {
    games: GameViewModel[];
    tracking: GameTrackingInfo[];
    meta: ParsedGameZoneMeta;
};

const readZoneGames = (zone: Zone): Game[] => (zone.data.body[0]?.items as Game[] | undefined) ?? [];

/** Parses a zone slice into adapted games and tracking — used at SSR and client refetch boundaries. */
export const parseZoneSliceGames = (
    zone: Zone,
    gridLayout: PRISMIC_ZONE_LAYOUT,
    ctx: ClientGameContext,
    options?: Omit<AdaptGameOptions, 'region' | 'isMobile'>
): ParsedGameZone => {
    const items = readZoneGames(zone);
    const zoneGames = getZoneGames(items, gridLayout, { ...ctx, ...options });

    return {
        games: zoneGames.games,
        tracking: zoneGames.tracking,
        meta: {
            title: zone.data.title?.[0]?.text,
            categoryUid: zone.data.body[0]?.primary?.category?.uid,
            zoneUid: zone.uid,
            rotatingTitles: zone.data.rotatingTitles,
            isDynamicOrdering: zone.data.dynamic_zone ?? false
        }
    };
};
